/**
 * Commander Design System - Kiosk Theme
 *
 * Industrial utility aesthetic - clean, high-contrast, confidence-inspiring.
 * Optimised for touch interaction in warehouse/depot environments.
 */

import { type MantineThemeOverride, mergeThemeOverrides, rem } from '@mantine/core';
import { sharedTheme } from './shared';
import { touchTargets } from '../tokens/spacing';
import { fontFamily } from '../tokens/typography';

/**
 * Kiosk colour palette - light theme for visibility in varied lighting
 */
const kioskPalette = {
  /** Clean background */
  background: '#f4f4f4',
  /** Card/panel surface */
  surface: '#ffffff',
  /** Secondary surface */
  surfaceAlt: '#e8e8e8',
  /** Border colour */
  border: '#d0d0d0',
  /** Primary text */
  text: '#161616',
  /** Secondary text */
  textMuted: '#525252',
  /** Success state */
  success: '#24a148',
  /** Error state */
  error: '#da1e28',
  /** Primary action */
  primary: '#0f62fe',
};

/**
 * Kiosk-specific theme overrides
 */
const kioskOverrides: MantineThemeOverride = {
  /** Use display font for headings */
  headings: {
    fontFamily: fontFamily.display,
    fontWeight: '700',
    sizes: {
      h1: { fontSize: rem(48), lineHeight: '1.1' },
      h2: { fontSize: rem(36), lineHeight: '1.15' },
      h3: { fontSize: rem(28), lineHeight: '1.2' },
      h4: { fontSize: rem(22), lineHeight: '1.25' },
    },
  },

  /** Larger base font size for readability */
  fontSizes: {
    xs: rem(14),
    sm: rem(16),
    md: rem(20),
    lg: rem(24),
    xl: rem(32),
  },

  /** Increased spacing for touch */
  spacing: {
    xs: rem(8),
    sm: rem(12),
    md: rem(20),
    lg: rem(32),
    xl: rem(48),
  },

  other: {
    kioskPalette,
  },

  components: {
    /** Large buttons with industrial styling */
    Button: {
      defaultProps: {
        size: 'xl',
        radius: 'md',
      },
      styles: {
        root: {
          minHeight: touchTargets.kioskPrimary,
          fontSize: rem(22),
          fontWeight: 600,
          fontFamily: fontFamily.display,
          letterSpacing: '0.01em',
          padding: `${rem(16)} ${rem(40)}`,
          transition: 'all 150ms ease',
          '&:active': {
            transform: 'scale(0.98)',
          },
        },
      },
    },

    /** Large, high-contrast input fields */
    TextInput: {
      defaultProps: {
        size: 'xl',
        radius: 'md',
      },
      styles: {
        input: {
          minHeight: rem(64),
          fontSize: rem(24),
          fontFamily: fontFamily.mono,
          fontWeight: 500,
          textAlign: 'center',
          backgroundColor: kioskPalette.surface,
          border: `2px solid ${kioskPalette.border}`,
          '&:focus': {
            borderColor: kioskPalette.primary,
            boxShadow: `0 0 0 3px rgba(15, 98, 254, 0.2)`,
          },
        },
        label: {
          fontSize: rem(16),
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          marginBottom: rem(8),
        },
      },
    },

    /** Cards with industrial styling */
    Card: {
      defaultProps: {
        padding: 'xl',
        radius: 'md',
      },
      styles: {
        root: {
          backgroundColor: kioskPalette.surface,
          border: `2px solid ${kioskPalette.border}`,
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
        },
      },
    },

    /** Large, bold status badges */
    Badge: {
      defaultProps: {
        size: 'lg',
        radius: 'sm',
      },
      styles: {
        root: {
          padding: `${rem(12)} ${rem(20)}`,
          fontSize: rem(14),
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        },
      },
    },

    /** Full-width modals for kiosk */
    Modal: {
      defaultProps: {
        fullScreen: true,
        radius: 0,
      },
    },

    /** Large, clear notifications */
    Notification: {
      styles: {
        root: {
          padding: rem(24),
          border: '2px solid',
          borderRadius: rem(8),
        },
        title: {
          fontSize: rem(24),
          fontWeight: 700,
          fontFamily: fontFamily.display,
        },
        description: {
          fontSize: rem(18),
        },
      },
    },

    /** High-contrast checkboxes */
    Checkbox: {
      styles: {
        input: {
          width: rem(28),
          height: rem(28),
          borderWidth: 2,
          '&:checked': {
            backgroundColor: kioskPalette.primary,
            borderColor: kioskPalette.primary,
          },
        },
        label: {
          fontSize: rem(18),
          fontWeight: 500,
          paddingLeft: rem(12),
        },
      },
    },

    /** Stack with generous spacing */
    Stack: {
      defaultProps: {
        gap: 'lg',
      },
    },

    /** Title styling */
    Title: {
      styles: {
        root: {
          color: kioskPalette.text,
        },
      },
    },

    /** Text styling */
    Text: {
      styles: {
        root: {
          color: kioskPalette.text,
        },
      },
    },
  },
};

/**
 * Complete kiosk theme - industrial utility aesthetic
 */
export const kioskTheme = mergeThemeOverrides(sharedTheme, kioskOverrides);
